import { FC, useEffect, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useDebounce } from 'hooks/useDebounce';

import CustomLoading from 'modules/_shared/components/CustomLoading';
import Table from 'modules/_shared/Table';
import { TablePagination } from 'modules/_shared/Table/components/TablePagination';
import { Eligible } from 'modules/transactions/pages/TenderOfferDetails/types/Eligible';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';

import IncentiveOfferEligiblesTableHeader from './IncentiveOfferEligiblesTableHeader';
import IncentiveOfferEligiblesTableBody from './IncentiveOfferEligiblesTableBody';

import './IncentiveOfferEligiblesTable.scss';

/* eslint-disable no-unused-vars -- callback param names in type definitions */
type IncentiveOfferEligiblesTableProps = {
  eligibles: Eligible[];
  page: number;
  rowsPerPage: number;
  totalRows: number;
  isLoading?: boolean;
  isDownloading?: boolean;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSearch: (searchTerm: string) => void;
  onDownload: () => void;
};
/* eslint-enable no-unused-vars */

const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 3;

const IncentiveOfferEligiblesTable: FC<IncentiveOfferEligiblesTableProps> = ({
  eligibles,
  page,
  rowsPerPage,
  totalRows,
  isLoading = false,
  isDownloading = false,
  onPageChange,
  onRowsPerPageChange,
  onSearch,
  onDownload,
}) => {
  const { t } = useTranslate();
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (debouncedSearch === '' || debouncedSearch.length >= MIN_SEARCH_LENGTH) {
      onSearch(debouncedSearch);
      return;
    }

    onSearch('');
  }, [debouncedSearch, onSearch]);

  const showPagination = totalRows > rowsPerPage;
  const showLoadingOverlay = isLoading || isDownloading;

  const tableContent = (
    <>
      <IncentiveOfferEligiblesTableHeader />
      <IncentiveOfferEligiblesTableBody
        eligibles={eligibles}
        page={page}
        rowsPerPage={rowsPerPage}
      />
    </>
  );

  return (
    <div
      className="card card-bordered mb-4 incentive-offer-eligibles-table"
      data-testid="incentive-offer-eligibles-table"
    >
      <div className="card-inner">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
          <h6 className="card-title mb-0">
            <em className="icon ni ni-user-list text-primary mr-2" />
            {t('EligibleBeneficiaries')}
          </h6>
        </div>

        <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div
            className="form-control-wrap mb-2 mb-md-0 mr-md-2"
            style={{ minWidth: '260px' }}
          >
            <div className="form-icon form-icon-left">
              <em className="icon ni ni-search" />
            </div>
            <input
              type="text"
              className="form-control"
              placeholder={[t('name'), t('email'), t('CIF')].join(', ')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              disabled={showLoadingOverlay}
              data-testid="incentive-offer-eligibles-search-input"
            />
          </div>
          <Button
            variant={ButtonVariants.LIGHT}
            onClick={onDownload}
            disabled={showLoadingOverlay || totalRows === 0}
            title={t('DownloadList')}
            data-testid="incentive-offer-eligibles-download-button"
            className="mb-2 mb-md-0"
          >
            <em className="icon ni ni-download mr-1" />
            <span>{t('DownloadList')}</span>
          </Button>
        </div>

        <Table scrollHorizontal content={tableContent} />

        {showPagination && (
          <TablePagination
            key={`${page}-${rowsPerPage}-${debouncedSearch}`}
            totalRows={totalRows}
            defaultPage={page}
            defaultRowsPerPage={rowsPerPage}
            onChangePage={onPageChange}
            onChangeRowsPerPage={onRowsPerPageChange}
          />
        )}
      </div>

      {showLoadingOverlay && <CustomLoading />}
    </div>
  );
};

export default IncentiveOfferEligiblesTable;
